import type { Notifications } from '@archipro-website/top-navigation/src/lib/types';
import type {
    HeaderNavLinks,
    HeaderUserData,
} from '@rocco/ui/website-header/components/Header';
import type { CountryConfig } from '@archipro-website/localisation';
import { PagesEnum, PROFILE_NAV } from '../../../utils/route-config';
import { groupProfessionalsByCountry } from '../../../utils/group-professionals-by-country';
import { professionalBusinessHref } from '../../../utils/professional-business-href';
import {
    getInboxUnreadCount,
    getTotalNotificationCount,
} from '../../../modules/notifications/utils';

// Cannot use User type from ~/server/types because this function is used by
// packages-manager and the import aliases will not work.
// The packages-manager UserState extends Me but can have __typename: 'Guest',
// so we need to accept either a proper Guest or a Me-like object with Guest typename
interface UserMe {
    __typename: 'Me';
    FirstName: string;
    LastName: string;
    ProfileImage: string;
    OrderNotificationCount: number;
    Professionals?: Array<{
        ID: number;
        Title: string;
        CompanyLogo: string;
        CompanyLogo48Resized?: string | null;
        CompanyLogo96Resized?: string | null;
        LogoBackgroundColor?: string | null;
        URLSegment: string;
        Country?: string | null;
        PrimaryDomain?: string | null;
    }>;
}

type UserGuest = { __typename: 'Guest' } & Record<string, unknown>;

// Support both strict discriminated unions and objects with union-typed __typename
type User =
    | UserMe
    | UserGuest
    | (Omit<UserMe, '__typename'> & { __typename: 'Me' | 'Guest' });

type MarketNavCapabilities = Pick<
    CountryConfig,
    'allowMemberGuides' | 'allowMemberOrders' | 'allowMemberTickets'
>;

/**
 * Returns profile menu links that are available in the current app feature set.
 */
const getProfileLinks = (
    buildHubEnabled: boolean,
    eventTicketingEnabled: boolean,
    marketNavCapabilities?: MarketNavCapabilities,
    isAdmin: boolean = false,
    getMatchedEnabled: boolean = false,
) => {
    return PROFILE_NAV.links.filter((link) => {
        if ('adminOnly' in link && link.adminOnly && !isAdmin) {
            return false;
        }
        if (
            link.to === PagesEnum.GET_MATCHED_REQUESTS ||
            link.to === PagesEnum.GET_MATCHED_REQUESTS_ADMIN
        ) {
            return getMatchedEnabled;
        }
        if (link.to === PagesEnum.MY_PROJECTS) {
            return buildHubEnabled;
        }
        if (link.to === PagesEnum.MY_TICKETS) {
            return (
                eventTicketingEnabled &&
                (marketNavCapabilities?.allowMemberTickets ?? true)
            );
        }
        if (link.to === PagesEnum.MY_GUIDES) {
            return marketNavCapabilities?.allowMemberGuides ?? true;
        }
        if (link.to === PagesEnum.MY_ORDERS) {
            return marketNavCapabilities?.allowMemberOrders ?? true;
        }
        return true;
    });
};

/**
 * Generates header data for the current user including profile links and user information
 * @param user - The current user object
 * @param notifications - User's notification counts across different sections
 * @param buildHubEnabled - Optional flag to enable/disable BuildHub features (default: false)
 * @param currentCountryCode - The country code where the user is currently browsing from
 * @param eventTicketingEnabled - Optional flag to show/hide My Event Tickets in the profile menu
 * @param newChatUI2025Enabled - The `new_chat_ui_2025` feature flag is enabled
 * @param marketNavCapabilities - Per-market member surface availability from Country config
 * @returns Object containing header user data and profile navigation links
 */
export const getUserHeaderData = (
    user: User,
    notifications: Notifications,
    buildHubEnabled: boolean = false,
    currentCountryCode?: string,
    eventTicketingEnabled: boolean = false,
    newChatUI2025Enabled: boolean = false,
    marketNavCapabilities?: MarketNavCapabilities,
    isAdmin: boolean = false,
    getMatchedEnabled: boolean = false,
): {
    headerUserData: HeaderUserData;
    profileLinks: HeaderNavLinks;
} => {
    // Initialize with guest/default values
    let headerUserData: HeaderUserData = {
        isAuthorized: false,
        firstName: 'Guest',
        notificationsCount: 0,
        professionalSections: [],
        showCountryLabels: false,
        orderNotificationCount: 0,
        inboxMenu: LEGACY_INBOX_MENU,
        inboxCount: 0,
        designBoardCount: 0,
        websiteNotificationCount: 0,
        isBuildHubEnabled: buildHubEnabled,
        isGetMatchedEnabled: getMatchedEnabled,
    };
    // Filter out "My Projects" if BuildHub is not enabled
    let profileLinks: HeaderNavLinks = {
        ...PROFILE_NAV,
        links: getProfileLinks(
            buildHubEnabled,
            eventTicketingEnabled,
            marketNavCapabilities,
            isAdmin,
            getMatchedEnabled,
        ),
    };

    // Only process authenticated users
    if (user.__typename === 'Me') {
        const inboxMenu = newChatUI2025Enabled
            ? NEW_INBOX_MENU
            : LEGACY_INBOX_MENU;
        const inboxCount = getInboxUnreadCount(
            notifications,
            newChatUI2025Enabled,
        );

        // Calculate total notifications across all notification sources
        const notificationsCount = getTotalNotificationCount(
            notifications,
            user.OrderNotificationCount,
            newChatUI2025Enabled,
        );

        // Transform professionals data into header format
        const professionals =
            user.Professionals?.map((prof) => ({
                id: prof.ID,
                title: prof.Title,
                logo: prof.CompanyLogo,
                companyLogo48Resized: prof.CompanyLogo48Resized ?? undefined,
                companyLogo96Resized: prof.CompanyLogo96Resized ?? undefined,
                logoBackgroundColor: prof.LogoBackgroundColor ?? undefined,
                urlSegment: prof.URLSegment,
                href: professionalBusinessHref(prof.URLSegment, {
                    country: prof.Country,
                    primaryDomain: prof.PrimaryDomain,
                    currentCountryCode,
                }),
                country: prof.Country ?? undefined,
                // Get unread count for this professional's inbox
                unreads:
                    notifications.companyInboxes.find(
                        (inbox) => inbox.professionalID === prof.ID,
                    )?.count ?? 0,
            })) ?? [];

        // Group professionals by country
        const { sections, showCountryLabels } = groupProfessionalsByCountry(
            professionals,
            currentCountryCode,
        );

        // Transform user data into header format
        headerUserData = {
            isAuthorized: true,
            avatarSrc: user.ProfileImage,
            firstName: user.FirstName,
            lastName: user.LastName,
            isBuildHubEnabled: buildHubEnabled,
            isGetMatchedEnabled: getMatchedEnabled,
            notificationsCount,
            professionalSections: sections,
            showCountryLabels,
            orderNotificationCount: user.OrderNotificationCount ?? 0,
            inboxMenu,
            inboxCount,
            designBoardCount: notifications.designBoard?.count ?? 0,
            websiteNotificationCount: notifications.website?.count ?? 0,
        };

        // Map notification counts to their respective pages
        const badgeCountMap: { [path: string]: number } = {
            [PagesEnum.LEGACY_INBOX]: inboxCount,
            [PagesEnum.INBOX]: inboxCount,
            '/app/boards': 0,
            [PagesEnum.MY_NOTIFICATIONS]: notifications.website?.count ?? 0,
            '/member/orders': user.OrderNotificationCount ?? 0,
        };

        // Add badge counts to profile navigation links
        // Filter out "My Projects" if BuildHub is not enabled
        profileLinks = {
            ...PROFILE_NAV,
            links: getProfileLinks(
                buildHubEnabled,
                eventTicketingEnabled,
                marketNavCapabilities,
                isAdmin,
                getMatchedEnabled,
            ).map((link) => {
                if (link.to !== PagesEnum.LEGACY_INBOX) {
                    return {
                        ...link,
                        badgeCount: badgeCountMap[link.to] ?? 0,
                    };
                }
                return {
                    title: inboxMenu.title,
                    to: inboxMenu.to,
                    isNonRemixRoute: inboxMenu.isNonRemixRoute,
                    badgeCount: inboxCount,
                };
            }),
        };
    }

    return { headerUserData, profileLinks };
};

const LEGACY_INBOX_MENU = {
    title: 'Personal Inbox',
    to: PagesEnum.LEGACY_INBOX,
    isNonRemixRoute: true,
} as const;

const NEW_INBOX_MENU = {
    title: 'Inbox',
    to: PagesEnum.INBOX,
    isNonRemixRoute: false,
} as const;
